import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import i18n, { detectLocale, SUPPORTED_LOCALES, formatCurrency, formatDate, formatTime, COUNTRY_PRESETS, CURRENCIES } from '../i18n';
import { UPDATE_LOCALE_MUTATION } from '../graphql';
import { useAuth } from './AuthContext';

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  const { currentUser } = useAuth();
  const [updateLocaleMutation] = useMutation(UPDATE_LOCALE_MUTATION, { ignoreResults: true });

  const [locale, setLocaleState] = useState(() => detectLocale(currentUser?.locale));
  const [currency, setCurrencyState] = useState(() => currentUser?.currency || 'INR');
  const [timezone, setTimezoneState] = useState(() => currentUser?.location?.timezone || 'Asia/Kolkata');

  // Sync with user profile on login / profile refresh
  useEffect(() => {
    if (currentUser) {
      const newLocale = currentUser.locale || detectLocale();
      const newCurrency = currentUser.currency || 'INR';
      const newTimezone = currentUser.location?.timezone || 'Asia/Kolkata';
      setLocaleState(newLocale);
      setCurrencyState(newCurrency);
      setTimezoneState(newTimezone);
      i18n.changeLanguage(newLocale);
      // Set HTML dir for RTL support (Arabic etc.)
      const dir = SUPPORTED_LOCALES.find(l => l.code === newLocale)?.dir || 'ltr';
      document.documentElement.setAttribute('dir', dir);
      document.documentElement.setAttribute('lang', newLocale.split('-')[0]);
    }
  }, [currentUser?.locale, currentUser?.currency, currentUser?.location?.timezone]); // eslint-disable-line react-hooks/exhaustive-deps

  const setLocale = useCallback(async (newLocale, persist = true) => {
    setLocaleState(newLocale);
    i18n.changeLanguage(newLocale);
    const dir = SUPPORTED_LOCALES.find(l => l.code === newLocale)?.dir || 'ltr';
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', newLocale.split('-')[0]);
    if (persist && currentUser) {
      const matchedCurrency = SUPPORTED_LOCALES.find(l => l.code === newLocale)?.currency;
      if (matchedCurrency) setCurrencyState(matchedCurrency);
      await updateLocaleMutation({ variables: { locale: newLocale, currency: matchedCurrency } }).catch(() => {});
    }
  }, [currentUser, updateLocaleMutation]);

  const setCurrency = useCallback(async (newCurrency, persist = true) => {
    setCurrencyState(newCurrency);
    if (persist && currentUser) {
      await updateLocaleMutation({ variables: { currency: newCurrency } }).catch(() => {});
    }
  }, [currentUser, updateLocaleMutation]);

  const applyCountryPreset = useCallback(async (countryCode) => {
    const preset = COUNTRY_PRESETS[countryCode];
    if (!preset) return;
    await setLocale(preset.locale, true);
    await setCurrency(preset.currency, false); // already persisted in setLocale
    setTimezoneState(preset.timezone);
    if (currentUser) {
      await updateLocaleMutation({ variables: { locale: preset.locale, currency: preset.currency, timezone: preset.timezone } }).catch(() => {});
    }
  }, [setLocale, setCurrency, currentUser, updateLocaleMutation]);

  // Format helpers that automatically use the current locale/currency/timezone
  const formatMoney = useCallback((amount, overrideCurrency) => {
    return formatCurrency(amount, overrideCurrency || currency, locale);
  }, [currency, locale]);

  const formatLocalDate = useCallback((dateString, options) => {
    return formatDate(dateString, locale, options);
  }, [locale]);

  const formatLocalTime = useCallback((dateString) => {
    return formatTime(dateString, locale, timezone);
  }, [locale, timezone]);

  const currencyInfo = CURRENCIES[currency] || CURRENCIES.INR;
  const localeInfo = SUPPORTED_LOCALES.find(l => l.code === locale) || SUPPORTED_LOCALES[0];
  const isRTL = localeInfo.dir === 'rtl';

  return (
    <LocaleContext.Provider value={{
      locale, currency, timezone, isRTL,
      currencyInfo, localeInfo,
      setLocale, setCurrency, applyCountryPreset,
      formatMoney, formatLocalDate, formatLocalTime,
    }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used inside LocaleProvider');
  return ctx;
}
